import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, ExternalLink, Plus, Trash2 } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { useAdmin } from "@/context/AdminContext";
import { api, errText } from "@/lib/api";

const PageRow = ({ p, i, count, onMove, onToggle, onRename, onDelete, onOpen }) => (
  <li className="border-b border-black/10 py-3" data-testid={`pages-row-${p.slug}`}>
    <div className="flex items-center gap-2">
      <input defaultValue={p.title} onBlur={(e) => e.target.value.trim() && e.target.value !== p.title && onRename(p, e.target.value.trim())}
        className="min-w-0 flex-1 bg-transparent text-base font-bold outline-none focus:underline" data-testid={`pages-title-${p.slug}`} aria-label="Page name" />
      <button onClick={() => onOpen(p)} aria-label="Open page" className="p-1.5 hover:text-[#E62314]" data-testid={`pages-open-${p.slug}`}><ExternalLink className="h-4 w-4" /></button>
      <button disabled={i === 0} onClick={() => onMove(i, -1)} aria-label="Move up" className="p-1.5 hover:text-[#E62314] disabled:opacity-30" data-testid={`pages-up-${p.slug}`}><ArrowUp className="h-4 w-4" /></button>
      <button disabled={i === count - 1} onClick={() => onMove(i, 1)} aria-label="Move down" className="p-1.5 hover:text-[#E62314] disabled:opacity-30" data-testid={`pages-down-${p.slug}`}><ArrowDown className="h-4 w-4" /></button>
      {!p.builtin && <button onClick={() => onDelete(p)} aria-label="Delete page" className="p-1.5 hover:text-[#E62314]" data-testid={`pages-delete-${p.slug}`}><Trash2 className="h-4 w-4" /></button>}
    </div>
    <label className="mt-2 flex items-center gap-2 text-xs text-[#57534E]">
      <Switch checked={p.show_in_menu} onCheckedChange={(v) => onToggle(p, v)} data-testid={`pages-menu-${p.slug}`} /> Show in top menu
      <span className="ml-auto font-mono-label">{p.path}</span>
    </label>
  </li>
);

export const PagesPanel = ({ open, onOpenChange }) => {
  const { pages, refreshPages } = useAdmin();
  const [title, setTitle] = useState("");
  const navigate = useNavigate();

  const saveMenu = async (list) => {
    try {
      await api.put("/menu", list.map((p, idx) => ({ slug: p.slug, show_in_menu: p.show_in_menu, menu_order: idx * 10 })));
      await refreshPages();
    } catch (e) { toast.error(errText(e)); }
  };
  const move = (i, d) => { const l = [...pages]; [l[i], l[i + d]] = [l[i + d], l[i]]; saveMenu(l); };
  const toggle = (p, v) => saveMenu(pages.map((x) => (x.slug === p.slug ? { ...x, show_in_menu: v } : x)));
  const rename = async (p, t) => { try { await api.put(`/pages/${p.slug}`, { title: t }); await refreshPages(); toast.success("Page renamed"); } catch (e) { toast.error(errText(e)); } };
  const remove = async (p) => {
    if (!window.confirm(`Delete the page "${p.title}"? This can't be undone.`)) return;
    try { await api.delete(`/pages/${p.slug}`); await refreshPages(); toast.success("Page deleted"); } catch (e) { toast.error(errText(e)); }
  };
  const create = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    try {
      const { data } = await api.post("/pages", { title: title.trim(), show_in_menu: true });
      setTitle(""); await refreshPages(); onOpenChange(false); navigate(data.path);
      toast.success("Page created. Add your content with the + buttons.");
    } catch (err) { toast.error(errText(err)); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent data-lenis-prevent data-testid="pages-panel" className="w-full overflow-y-auto bg-[#FBF9F5] sm:max-w-md">
        <SheetHeader className="text-left">
          <SheetTitle className="font-display text-2xl font-extrabold">Pages</SheetTitle>
          <SheetDescription>Create pages, rename them, choose which show in the top menu and in what order.</SheetDescription>
        </SheetHeader>
        <form onSubmit={create} className="mt-6 flex gap-2">
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="New page name, e.g. Gallery" data-testid="pages-new-title"
            className="min-w-0 flex-1 border border-[#E62314] bg-white/60 px-3 py-2.5 text-sm outline-none" />
          <button type="submit" data-testid="pages-create" className="flex items-center gap-1 bg-[#0A0A0A] px-4 text-sm font-bold text-white hover:bg-[#E62314]"><Plus className="h-4 w-4" />Create</button>
        </form>
        <ul className="mt-6">
          {pages.map((p, i) => <PageRow key={p.slug} p={p} i={i} count={pages.length} onMove={move} onToggle={toggle} onRename={rename} onDelete={remove} onOpen={(x) => { onOpenChange(false); navigate(x.path); }} />)}
        </ul>
      </SheetContent>
    </Sheet>
  );
};
